import React from "react";
import { FaAngleDoubleRight, FaPlus, FaTrash, FaFileAlt } from "react-icons/fa";
import Select from "react-select";
import "./DynamicForm.css"; // Ensure this CSS file exists for styling
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DynamicForm = ({
  formName,
  formConfig,
  formData,
  onChange,
  onSubmit,
  onBack,
  isEditing,
  activeTab,
  setActiveTab,
  tabs,
  errors = {},
  files = {}, // store preview URLs or file names
  setFiles,
  onDownload,
  validateTab,
  ...props
}) => {
  const renderField = (field, parentIndex = null, parentName = null) => {
    // Determine the current field's value, handling nested dynamicList
    const fieldValue = parentName
      ? formData[parentName]?.[parentIndex]?.[field.name] ?? ""
      : formData[field.name] ?? "";

    // Error for specific field. For nested fields (like in dynamicList or future table types),
    // errors object needs to be structured accordingly.
    // For tables, row errors are handled within renderTableSection.
    const error =
      errors[field.name] ||
      (parentName && errors[`${parentName}[${parentIndex}].${field.name}`]);

    const baseInputProps = {
      className: `form-control form-control-sm ${error ? "is-invalid" : ""}`,
      name: field.name,
      placeholder: field.placeholder,
      required: field.required,
      readOnly: field.readOnly,
      min: field.min,
      max: field.max,
    };

    const handleChange = (value) => {
      if (parentName) {
        // Handle changes for fields within a dynamicList
        const updatedList = [...(formData[parentName] || [])];
        if (updatedList[parentIndex]) {
          updatedList[parentIndex] = {
            ...updatedList[parentIndex],
            [field.name]: value,
          };
        }
        onChange({
          target: { name: parentName, value: updatedList },
        });
      } else {
        // Handle changes for top-level fields
        onChange({
          target: { name: field.name, value: value },
        });
      }
    };

    switch (field.type) {
      case "text":
      case "email":
      case "number":
        return (
          <>
            <input
              {...baseInputProps}
              type={field.type}
              value={fieldValue}
              onChange={(e) => handleChange(e.target.value)}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </>
        );

      case "textarea":
        return (
          <>
            <textarea
              {...baseInputProps}
              value={fieldValue}
              onChange={(e) => handleChange(e.target.value)}
              rows={field.rows || 2}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </>
        );

      case "select":
        // Resolve dynamic options
        const dynamicOptions =
          field.options ||
          (field.source && Array.isArray(props[field.source])
            ? props[field.source].map((d) => ({
                value: d.name || d.name,
                label: d.name,
              }))
            : []);

        return (
          <>
            <Select
              classNamePrefix="react-select"
              className={`${error ? "is-invalid-select" : ""}`}
              name={field.name}
              options={dynamicOptions}
              value={dynamicOptions.find((opt) => opt.value === fieldValue)}
              onChange={(selectedOption) =>
                handleChange(selectedOption ? selectedOption.value : "")
              }
              placeholder={field.placeholder || `Select ${field.label}`}
              isClearable={!field.required}
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: error ? "#dc3545" : base.borderColor,
                  minHeight: "31px",
                  height: "31px",
                  fontSize: "0.875rem",
                  boxShadow: error
                    ? "0 0 0 0.25rem rgba(220,53,69,.25)"
                    : "none",
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: "27px",
                  padding: "0 8px",
                }),
                input: (base) => ({ ...base, margin: "0px" }),
                indicatorSeparator: (base) => ({ ...base, display: "none" }),
                dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
              }}
            />
            {error && <div className="text-danger mt-1 small">{error}</div>}
          </>
        );

      case "file": {
        const existingFileName = fieldValue; // backend file name
        const fileData = files[field.name]; // uploaded file info
        //const filePreviewUrl = fileData?.previewUrl;
        const displayName = fileData?.name || existingFileName;

        // check if extension is image
        // const isImageExt = (name) => {
        //   if (!name) return false;
        //   return /\.(jpg|jpeg|png|gif)$/i.test(name);
        // };

        // const showImage =
        //   (filePreviewUrl && fileData?.type?.startsWith("image/")) ||
        //   isImageExt(existingFileName);

        return (
          <div className="mt-2">
            <input
              type="file"
              className={`form-control form-control-sm ${
                error ? "is-invalid" : ""
              }`}
              name={field.name}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > 1048576) {
                  toast.error("File size should not exceed 1 MB");
                  return;
                }

                if (props.onUpload) {
                  await props.onUpload(field.name, file);
                }
              }}
              accept={field.accept || "*/*"}
            />

            {/* Preview / Existing file */}
            <div className="border rounded p-2 text-center mt-2 bg-light d-flex flex-column align-items-center">
              <>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <FaFileAlt size={20} className="me-2 text-secondary" />
                  <span>{displayName}</span>
                </div>
                {displayName && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mt-1"
                    onClick={() => onDownload(existingFileName)}
                  >
                    ⬇ Download
                  </button>
                )}
              </>
            </div>

            {field.hint && (
              <small className="text-muted d-block mt-1">{field.hint}</small>
            )}
            {error && <div className="text-danger small mt-1">{error}</div>}
          </div>
        );
      }

      case "dynamicList": // For nested dynamic lists if you have them
        const listItems = formData[field.name] || [];
        return (
          <>
            {listItems.map((item, index) => (
              <div
                key={index}
                className="conversion-card mb-3 p-3 border rounded"
              >
                <div className="row g-3">
                  {field.subFields.map((subField) => (
                    <div
                      key={subField.name}
                      className={`col-md-${subField.cols || 6} col-12`}
                    >
                      <label className="form-label fw-semibold small">
                        {subField.label}
                        {subField.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      {renderField(subField, index, field.name)}
                    </div>
                  ))}
                </div>

                <div className="text-end mt-2">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      const updatedList = [...(formData[field.name] || [])];
                      updatedList.splice(index, 1);
                      onChange({
                        target: { name: field.name, value: updatedList },
                      });
                    }}
                  >
                    <FaTrash className="me-1" />
                    {field.removeButtonText || "Remove"}
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-3">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  const newItem = {};
                  field.subFields.forEach((subField) => {
                    newItem[subField.name] =
                      subField.type === "number" ? 0 : "";
                    if (
                      subField.type === "switch" ||
                      subField.type === "checkbox"
                    ) {
                      newItem[subField.name] = false;
                    }
                  });

                  const updatedList = [
                    ...(formData[field.name] || []),
                    newItem,
                  ];
                  onChange({
                    target: { name: field.name, value: updatedList },
                  });
                }}
              >
                <FaPlus className="me-1" />
                {field.addButtonText || "Add Item"}
              </button>
            </div>
          </>
        );

      case "switch":
        return (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={field.name}
              name={field.name}
              checked={fieldValue || false}
              onChange={(e) => handleChange(e.target.checked)}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </div>
        );

      case "date":
        return (
          <>
            <input
              {...baseInputProps}
              type="date"
              value={fieldValue}
              onChange={(e) => handleChange(e.target.value)}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </>
        );

      case "checkbox":
        return (
          <div className="form-check form-check-inline mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id={field.name}
              name={field.name}
              checked={fieldValue || false}
              onChange={(e) => handleChange(e.target.checked)}
            />
            {error && <div className="invalid-feedback">{error}</div>}
          </div>
        );

      case "radio":
        return (
          <div className="d-flex align-items-center mt-2">
            {field.options.map((option) => (
              <div
                className="form-check form-check-inline"
                key={String(option.value)}
              >
                <input
                  className="form-check-input"
                  type="radio"
                  name={field.name}
                  id={`${field.name}-${String(option.value).toLowerCase()}`}
                  value={String(option.value)}
                  checked={fieldValue === option.value}
                  onChange={() => handleChange(option.value)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${field.name}-${String(
                    option.value
                  ).toLowerCase()}`}
                >
                  {option.label}
                </label>
              </div>
            ))}
            {error && <div className="invalid-feedback">{error}</div>}
          </div>
        );

      case "table": // Handle table rendering directly as a "field"
        return renderTableSection(field);

      default:
        console.warn(
          `Unknown field type: ${field.type} for field ${field.name}`
        );
        return null;
    }
  };

  const renderTableSection = (section) => {
    // Table data should be stored under the section's name in formData
    const tableData = formData[section.name] || []; // Use section.name as the key for table data

    const handleTableChange = (value, rowIndex, colName) => {
      const updatedTableData = [...tableData];
      updatedTableData[rowIndex] = {
        ...updatedTableData[rowIndex],
        [colName]: value,
      };
      onChange({
        target: {
          name: section.name, // Update the entire array for this section/field
          value: updatedTableData,
        },
      });
    };

    const handleAddItem = () => {
      const newItem = {};
      section.columns.forEach((col) => {
        if (col.type === "number") {
          newItem[col.name] = 0;
        } else if (col.type === "checkbox" || col.type === "switch") {
          newItem[col.name] = false;
        } else if (col.type === "select") {
          // Default to the first option's value if available
          newItem[col.name] =
            col.options && col.options.length > 0 ? col.options[0].value : "";
        } else {
          newItem[col.name] = "";
        }
      });
      onChange({
        target: {
          name: section.name,
          value: [...tableData, newItem],
        },
      });
    };

    const handleRemoveItem = (indexToRemove) => {
      const updatedTableData = tableData.filter(
        (_, index) => index !== indexToRemove
      );
      onChange({
        target: {
          name: section.name,
          value: updatedTableData,
        },
      });
    };

    return (
      <div className="table-responsive my-3">
        {" "}
        {/* Added margin for spacing */}
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              {section.columns.map((column) => (
                <th key={column.name}>
                  {column.label}
                  {column.required && <span className="text-danger">*</span>}
                </th>
              ))}
              <th style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 && (
              <tr>
                <td
                  colSpan={section.columns.length + 1}
                  className="text-center text-muted py-3"
                >
                  No items added. Click "Add Item" to add one.
                </td>
              </tr>
            )}
            {tableData.map((row, rowIndex) => {
              // Construct error key for table rows: e.g., "itemsTable[0].itemName"
              const rowErrors = errors[`${section.name}[${rowIndex}]`] || {};

              return (
                <tr key={rowIndex}>
                  {section.columns.map((column) => {
                    const colError = rowErrors[column.name];
                    const colValue = row[column.name] ?? "";

                    return (
                      <td key={column.name}>
                        {column.type === "select" ? (
                          <Select
                            classNamePrefix="react-select"
                            className={`${colError ? "is-invalid-select" : ""}`}
                            options={column.options}
                            value={column.options.find(
                              (opt) => opt.value === colValue
                            )}
                            onChange={(selected) =>
                              handleTableChange(
                                selected ? selected.value : "",
                                rowIndex,
                                column.name
                              )
                            }
                            placeholder={
                              column.placeholder || `Select ${column.label}`
                            }
                            isClearable={!column.required}
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor: colError
                                  ? "#dc3545"
                                  : base.borderColor,
                                minHeight: "31px",
                                height: "31px",
                                fontSize: "0.875rem",
                                boxShadow: colError
                                  ? "0 0 0 0.25rem rgba(220,53,69,.25)"
                                  : "none",
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "27px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({ ...base, margin: "0px" }),
                              indicatorSeparator: (base) => ({
                                ...base,
                                display: "none",
                              }),
                              dropdownIndicator: (base) => ({
                                ...base,
                                padding: "4px",
                              }),
                            }}
                          />
                        ) : (
                          <input
                            type={column.type}
                            className={`form-control form-control-sm ${
                              colError ? "is-invalid" : ""
                            }`}
                            value={colValue}
                            onChange={(e) =>
                              handleTableChange(
                                e.target.value,
                                rowIndex,
                                column.name
                              )
                            }
                            placeholder={column.placeholder}
                            required={column.required}
                            min={column.min}
                            max={column.max}
                          />
                        )}
                        {colError && (
                          <div className="invalid-feedback">{colError}</div>
                        )}
                      </td>
                    );
                  })}
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveItem(rowIndex)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleAddItem}
          >
            <FaPlus className="me-1" />
            {section.addButtonText || "Add Item"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body p-3">
        <h5 className="card-title mb-3">
          {isEditing ? `Edit ${formName}` : `Add ${formName}`}
        </h5>

        {/* Nav Tabs - remains the same, as you define tabs separately */}
        {tabs && (
          <ul className="nav nav-tabs nav-tabs-sm mb-3">
            {tabs
              .filter((tab) => !tab.condition || tab.condition(formData))
              .map((tab) => (
                <li className="nav-item" key={tab.id}>
                  <button
                    className={`nav-link py-1 px-3 ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
          </ul>
        )}

        {/* Tab Content */}
        <div className="tab-content bg-white p-2 rounded-bottom border border-top-0">
          {formConfig
            .filter(
              (section) =>
                (!section.condition || section.condition(formData)) &&
                activeTab === section.id // Only show the active tab's content
            )
            .map((section) => (
              <div key={section.id} style={{ display: "block" }}>
                {" "}
                {/* Display is always block because of the filter above */}
                <div className="row g-2">
                  {section.fields.map((field) => (
                    <div
                      key={field.name}
                      className={`col-md-${field.cols || 6} mb-2`}
                    >
                      <label className="form-label small fw-semibold mb-1">
                        {field.label}
                        {field.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
                {/* Buttons - Only show buttons if not a table section or if it's the last section (regardless of type) */}
                <div className="d-flex justify-content-end mt-3 gap-2">
                  {section.id !== tabs[tabs.length - 1]?.id &&
                  tabs.length > 1 ? ( // Only show "Next" if not last tab and more than 1 tab
                    // In your DynamicForm component, update the Next button onClick:
                    <button
                      className="btn btn-sm bluebtn"
                      onClick={() => {
                        // Validate only current tab fields
                        if (!validateTab(activeTab)) {
                          toast.error(
                            "Please fix validation errors in current tab before proceeding.",
                            {
                              toastId: "tab-validation-error",
                            }
                          );
                          return;
                        }

                        // Move to next tab if validation passes
                        const currentIndex = tabs.findIndex(
                          (t) => t.id === activeTab
                        );
                        if (currentIndex < tabs.length - 1) {
                          setActiveTab(tabs[currentIndex + 1].id);
                        }
                      }}
                    >
                      Next <FaAngleDoubleRight size={12} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm bluebtn"
                      onClick={onSubmit}
                    >
                      {isEditing ? "Update" : "Save"} {formName}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={onBack}
                  >
                    Back
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DynamicForm;
